import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

export interface PositionMatrixEntry {
  id: string;
  label_de: string;
  label_en: string;
  aliases: string[];
  is_active: boolean;
  sort_order: number;
  has_override?: boolean;
  default_sort_order?: number;
  default_is_active?: boolean;
}

interface InvestorType { id: string; code: string; label_de: string; sort_order: number; }

const inp: React.CSSProperties = {
  width: '100%', padding: '6px 10px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: 12, background: 'var(--surface)', color: 'var(--text)',
};
const btn = (variant: 'primary' | 'ghost' | 'danger' = 'ghost'): React.CSSProperties => ({
  padding: '4px 10px', fontSize: 11, borderRadius: 'var(--radius)', cursor: 'pointer',
  background: variant === 'primary' ? 'var(--accent)' : variant === 'danger' ? 'var(--accent-light)' : 'var(--surface2)',
  color: variant === 'primary' ? '#fff' : variant === 'danger' ? 'var(--accent)' : 'var(--text2)',
  border: variant === 'primary' ? 'none' : '1px solid var(--border)',
  fontWeight: variant === 'primary' ? 500 : 400,
});

interface RowProps {
  pos: PositionMatrixEntry;
  index: number;
  total: number;
  showOverrideHint: boolean;
  onMove: (id: string, dir: -1 | 1) => void;
  onSave: (id: string, patch: Partial<PositionMatrixEntry>) => Promise<void>;
  onDelete: (id: string) => void;
  onResetOverride?: (id: string) => void;
}

function Row({ pos, index, total, showOverrideHint, onMove, onSave, onDelete, onResetOverride }: RowProps) {
  const [edit, setEdit] = useState(false);
  const [labelDe, setLabelDe] = useState(pos.label_de);
  const [labelEn, setLabelEn] = useState(pos.label_en);
  const [aliasesText, setAliasesText] = useState(pos.aliases.join(', '));
  const [busy, setBusy] = useState(false);

  const cancel = () => { setLabelDe(pos.label_de); setLabelEn(pos.label_en); setAliasesText(pos.aliases.join(', ')); setEdit(false); };
  const save = async () => {
    setBusy(true);
    await onSave(pos.id, {
      label_de: labelDe.trim(),
      label_en: labelEn.trim(),
      aliases: aliasesText.split(',').map(s => s.trim()).filter(Boolean),
    });
    setBusy(false); setEdit(false);
  };
  const toggleActive = () => onSave(pos.id, { is_active: !pos.is_active });

  return (
    <tr style={{ borderBottom: '1px solid var(--border)', opacity: pos.is_active ? 1 : 0.5 }}>
      <td style={{ padding: '8px 6px', width: 50, fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text3)', textAlign: 'right' }}>
        {String(pos.sort_order).padStart(2, '0')}
      </td>
      <td style={{ padding: '8px 6px', width: 70 }}>
        <button type="button" onClick={() => onMove(pos.id, -1)} disabled={index === 0} style={{ ...btn(), padding: '2px 6px', marginRight: 2, opacity: index === 0 ? 0.3 : 1 }}>↑</button>
        <button type="button" onClick={() => onMove(pos.id, 1)}  disabled={index === total - 1} style={{ ...btn(), padding: '2px 6px', opacity: index === total - 1 ? 0.3 : 1 }}>↓</button>
      </td>
      {edit ? (
        <>
          <td style={{ padding: '4px 6px' }}><input style={inp} value={labelDe} onChange={e => setLabelDe(e.target.value)} placeholder="Deutsch" /></td>
          <td style={{ padding: '4px 6px' }}><input style={inp} value={labelEn} onChange={e => setLabelEn(e.target.value)} placeholder="Englisch" /></td>
          <td style={{ padding: '4px 6px' }}><input style={inp} value={aliasesText} onChange={e => setAliasesText(e.target.value)} placeholder="Alias1, Alias2" /></td>
        </>
      ) : (
        <>
          <td style={{ padding: '8px 6px', fontSize: 12 }}>{pos.label_de}</td>
          <td style={{ padding: '8px 6px', fontSize: 12, color: 'var(--text2)' }}>{pos.label_en}</td>
          <td style={{ padding: '8px 6px', fontSize: 11, color: 'var(--text3)' }}>{pos.aliases.join(', ')}</td>
        </>
      )}
      <td style={{ padding: '8px 6px', width: 70, textAlign: 'center' }}>
        <input type="checkbox" checked={pos.is_active} onChange={toggleActive}
          title={pos.is_active ? 'Relevant — wird beim Postversand berücksichtigt' : 'Nicht relevant — wird ignoriert'} />
      </td>
      <td style={{ padding: '8px 6px', width: 180, whiteSpace: 'nowrap', textAlign: 'right' }}>
        {edit ? (
          <>
            <button type="button" onClick={save} disabled={busy} style={btn('primary')}>Speichern</button>
            <button type="button" onClick={cancel} disabled={busy} style={{ ...btn(), marginLeft: 4 }}>×</button>
          </>
        ) : showOverrideHint ? (
          // Type-Modus: Position aus dieser Investorenart entfernen (Override löschen)
          <button type="button" onClick={() => onResetOverride && onResetOverride(pos.id)}
            title="Position aus dieser Investorenart entfernen"
            style={btn('danger')}>× Entfernen</button>
        ) : (
          <>
            <button type="button" onClick={() => setEdit(true)} style={btn()}>Bearbeiten</button>
            <button type="button" onClick={() => onDelete(pos.id)} style={{ ...btn('danger'), marginLeft: 4 }}>Löschen</button>
          </>
        )}
      </td>
    </tr>
  );
}

export function PositionMatrixManager() {
  const [list, setList] = useState<PositionMatrixEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newDe, setNewDe] = useState('');
  const [newEn, setNewEn] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  // Investorenart-Switcher: '' = global default, sonst investor_type_id
  const [investorTypes, setInvestorTypes] = useState<InvestorType[]>([]);
  const [activeTypeId, setActiveTypeId] = useState<string>('');
  useEffect(() => {
    apiClient.get<{ data: InvestorType[] }>('/investor-types')
      .then(r => setInvestorTypes(r.data.data ?? [])).catch(() => {});
  }, []);

  const reload = () => {
    setLoading(true);
    const url = activeTypeId
      ? `/position-matrix?investor_type_id=${activeTypeId}`
      : '/position-matrix';
    apiClient.get<{ data: PositionMatrixEntry[] }>(url)
      .then(r => setList(r.data.data ?? []))
      .catch(() => setError('Konnte Positionen nicht laden.'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [activeTypeId]);

  const move = async (id: string, dir: -1 | 1) => {
    const idx = list.findIndex(p => p.id === id);
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[idx], next[target]] = [next[target], next[idx]];
    setList(next.map((p, i) => ({ ...p, sort_order: i + 1 })));
    const url = activeTypeId
      ? `/position-matrix/reorder/type/${activeTypeId}`
      : '/position-matrix/reorder/all';
    await apiClient.put(url, { ordered_ids: next.map(p => p.id) }).catch(() => reload());
  };

  const save = async (id: string, patch: Partial<PositionMatrixEntry>) => {
    if (activeTypeId) {
      // Override für diese Investorenart speichern statt globale Position zu ändern
      await apiClient.put('/position-matrix/override/upsert', {
        position_id: id, investor_type_id: activeTypeId,
        sort_order: patch.sort_order, is_active: patch.is_active,
      });
    } else {
      await apiClient.put(`/position-matrix/${id}`, patch);
    }
    setList(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const resetOverride = async (id: string) => {
    if (!activeTypeId) return;
    await apiClient.delete(`/position-matrix/override/${id}/${activeTypeId}`);
    reload();
  };

  const del = async (id: string) => {
    if (!confirm('Position wirklich löschen?')) return;
    await apiClient.delete(`/position-matrix/${id}`);
    setList(prev => prev.filter(p => p.id !== id));
  };

  const create = async () => {
    if (!newDe.trim() || !newEn.trim()) return;
    try {
      // 1. globale Position anlegen
      const r = await apiClient.post<{ data: PositionMatrixEntry }>('/position-matrix', {
        label_de: newDe.trim(), label_en: newEn.trim(), is_active: true,
      });
      // 2. wenn wir in einer Investorenart sind: Override anhängen
      if (activeTypeId) {
        await apiClient.post('/position-matrix/attach', {
          position_id: r.data.data.id,
          investor_type_id: activeTypeId,
          is_active: true,
        });
        reload();
      } else {
        setList(prev => [...prev, r.data.data]);
      }
      setNewDe(''); setNewEn(''); setAdding(false);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      alert(msg ?? 'Fehler beim Anlegen');
    }
  };

  // Bestehende globale Position der aktuellen Investorenart zuordnen
  const [attachOpen, setAttachOpen] = useState(false);
  const [poolPositions, setPoolPositions] = useState<PositionMatrixEntry[]>([]);
  const openAttach = async () => {
    const r = await apiClient.get<{ data: PositionMatrixEntry[] }>('/position-matrix');
    const usedIds = new Set(list.map(p => p.id));
    setPoolPositions(r.data.data.filter(p => !usedIds.has(p.id)));
    setAttachOpen(true);
  };
  const attach = async (positionId: string) => {
    await apiClient.post('/position-matrix/attach', {
      position_id: positionId, investor_type_id: activeTypeId, is_active: true,
    });
    setAttachOpen(false);
    reload();
  };

  const applyBulk = async () => {
    if (!confirm('Alle bestehenden Kontakte gemäß Matrix neu klassifizieren? (Hauptansprechpartner / Postempfänger werden überschrieben)')) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const r = await apiClient.post<{ data: { companies_processed: number; contacts_updated: number } }>('/position-matrix/apply-bulk');
      setBulkResult(`✓ ${r.data.data.companies_processed} Firmen geprüft · ${r.data.data.contacts_updated} Kontakte aktualisiert`);
    } catch {
      setBulkResult('✗ Fehler beim Anwenden');
    } finally { setBulkBusy(false); }
  };

  if (loading) return <div style={{ padding: 16, fontSize: 12, color: 'var(--text3)' }}>Laden…</div>;
  if (error)   return <div style={{ padding: 16, fontSize: 12, color: 'var(--accent)' }}>{error}</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.6 }}>
        Reihenfolge = Rang. Die <strong>höchstrangige</strong> aktive Position eines Investors wird automatisch
        zum <strong>Hauptansprechpartner</strong> (E-Mail-To). Alle weiteren aktiven Treffer werden <strong>Postempfänger</strong> (CC).
        Positionen, die <em>nicht</em> in der Liste stehen oder als nicht-relevant markiert sind, werden für den Versand ignoriert.
      </div>

      {/* Investorenart-Switcher */}
      <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 'var(--radius)', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Ansicht:</span>
        <button type="button" onClick={() => setActiveTypeId('')} style={{
          padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
          background: !activeTypeId ? 'var(--accent)' : 'var(--surface)',
          color:      !activeTypeId ? '#fff'         : 'var(--text2)',
          border: '1px solid var(--border)', fontWeight: !activeTypeId ? 600 : 400,
        }}>Global (Standard)</button>
        {investorTypes.map(t => (
          <button key={t.id} type="button" onClick={() => setActiveTypeId(t.id)} style={{
            padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
            background: activeTypeId === t.id ? 'var(--accent)' : 'var(--surface)',
            color:      activeTypeId === t.id ? '#fff'         : 'var(--text2)',
            border: '1px solid var(--border)', fontWeight: activeTypeId === t.id ? 600 : 400,
          }}>{t.label_de}</button>
        ))}
      </div>
      {activeTypeId && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
          Anpassungen für „{investorTypes.find(t => t.id === activeTypeId)?.label_de}". Positionen ohne eigenen Eintrag verwenden die globale Reihenfolge.
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left', color: 'var(--text3)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <th style={{ padding: '6px', textAlign: 'right' }}>Rang</th>
            <th style={{ padding: '6px' }}>Verschieben</th>
            <th style={{ padding: '6px' }}>Deutsch</th>
            <th style={{ padding: '6px' }}>Englisch</th>
            <th style={{ padding: '6px' }}>Aliase</th>
            <th style={{ padding: '6px', textAlign: 'center' }}>Relevant</th>
            <th style={{ padding: '6px', textAlign: 'right' }}>Aktion</th>
          </tr>
        </thead>
        <tbody>
          {list.map((p, i) => (
            <Row key={p.id} pos={p} index={i} total={list.length}
              showOverrideHint={!!activeTypeId}
              onMove={move} onSave={save} onDelete={del}
              onResetOverride={resetOverride}
            />
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {adding ? (
          <>
            <input style={{ ...inp, width: 240 }} value={newDe} onChange={e => setNewDe(e.target.value)} placeholder="Deutsch (z.B. 'Investment Analyst')" autoFocus />
            <input style={{ ...inp, width: 240 }} value={newEn} onChange={e => setNewEn(e.target.value)} placeholder="Englisch (z.B. 'Investment Analyst')" />
            <button type="button" onClick={create} style={btn('primary')}>Anlegen</button>
            <button type="button" onClick={() => { setAdding(false); setNewDe(''); setNewEn(''); }} style={btn()}>Abbrechen</button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setAdding(true)} style={btn('primary')}>+ Neue Position</button>
            {activeTypeId && (
              <button type="button" onClick={openAttach} style={btn()}>+ Aus Pool wählen</button>
            )}
          </>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={applyBulk} disabled={bulkBusy} style={btn()}>
          {bulkBusy ? 'Verarbeite…' : 'Auf alle Bestandskontakte anwenden'}
        </button>
        {bulkResult && <span style={{ fontSize: 11, color: bulkResult.startsWith('✓') ? '#2e7d32' : 'var(--accent)' }}>{bulkResult}</span>}
      </div>

      {attachOpen && (
        <div onClick={() => setAttachOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--surface)', borderRadius: 8, padding: 16, width: 480, maxHeight: '70vh', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Position aus Pool wählen</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
              Globale Positionen, die noch keiner Investorenart zugeordnet sind:
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              {poolPositions.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Alle Pool-Positionen sind bereits zugeordnet.</div>}
              {poolPositions.map(p => (
                <button key={p.id} type="button" onClick={() => attach(p.id)} style={{
                  textAlign: 'left', padding: '8px 10px', background: 'var(--surface2)',
                  border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                }}>
                  <strong>{p.label_de}</strong> <span style={{ color: 'var(--text3)' }}>· {p.label_en}</span>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button type="button" onClick={() => setAttachOpen(false)} style={btn()}>Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
